import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import { THEMES } from '../config/constants';
import Card from '../components/Card';
import { TrendingUp, Users, Package, FileText, ShieldAlert, BarChart3 } from 'lucide-react';
import { formatCurrency } from '../utils/helpers';

const COLORS = ['#3b82f6', '#ea580c', '#10b981', '#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4'];

const AnalyticsView = ({ quotes, customers, products, theme, currentUser, users }) => {
  // Filter data to only show for the current user (unless they are admin)
  const myQuotes = useMemo(() => {
    if (currentUser?.role === 'admin') return quotes;
    return quotes.filter(q => q.userId === currentUser?.id);
  }, [quotes, currentUser]);

  const myCustomers = useMemo(() => {
    if (currentUser?.role === 'admin') return customers;
    return customers.filter(c => c.userId === currentUser?.id);
  }, [customers, currentUser]);

  // Process revenue over last 6 months for MY quotes
  const revenueData = useMemo(() => {
    const data = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      data.push({
        name: d.toLocaleString('default', { month: 'short' }),
        revenue: 0,
        month: d.getMonth(),
        year: d.getFullYear()
      });
    }

    myQuotes.forEach(q => {
      if (q.status === 'Order Confirmed' || q.status === 'Sent' || q.status === 'Follow Up') {
        const qDate = new Date(q.date);
        const match = data.find(d => d.month === qDate.getMonth() && d.year === qDate.getFullYear());
        if (match) {
          match.revenue += (q.total || 0);
        }
      }
    });
    return data;
  }, [myQuotes]);

  // Conversion rate (Accepted vs Rejected/Draft) for MY quotes
  const conversionData = useMemo(() => {
    const statusCounts = { 'Order Confirmed': 0, 'Sent': 0, 'Draft': 0, 'Follow Up': 0, 'Rejected': 0 };
    myQuotes.forEach(q => {
      if (statusCounts[q.status] !== undefined) {
        statusCounts[q.status]++;
      }
    });
    return [
      { name: 'Won', value: statusCounts['Order Confirmed'], color: '#10b981' },
      { name: 'Pending', value: statusCounts['Sent'] + statusCounts['Follow Up'], color: '#f59e0b' },
      { name: 'Lost/Draft', value: statusCounts['Draft'] + statusCounts['Rejected'], color: '#ef4444' }
    ];
  }, [myQuotes]);

  // Top Products for MY quotes
  const topProducts = useMemo(() => {
    const productSales = {};
    myQuotes.forEach(q => {
      if (q.status !== 'Rejected') {
        q.items.forEach(item => {
          if (!productSales[item.name]) productSales[item.name] = { count: 0, revenue: 0 };
          const qty = Number(item.qty) || 1;
          const price = Number(item.price) || 0;
          productSales[item.name].count += qty;
          productSales[item.name].revenue += (price * qty);
        });
      }
    });
    return Object.entries(productSales)
      .map(([name, stats]) => ({ name, ...stats }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  }, [myQuotes]);

  const totalRevenue = revenueData.reduce((acc, curr) => acc + curr.revenue, 0);

  // --- ADMIN ONLY DATA ---
  const adminMonthlyData = useMemo(() => {
    if (currentUser?.role !== 'admin') return [];
    const data = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthObj = {
        name: d.toLocaleString('default', { month: 'short' }),
        month: d.getMonth(),
        year: d.getFullYear()
      };
      // initialize users
      users.forEach(u => monthObj[u.name] = 0);
      data.push(monthObj);
    }

    quotes.forEach(q => {
      if (q.status === 'Order Confirmed' || q.status === 'Sent' || q.status === 'Follow Up') {
        const qDate = new Date(q.date);
        const match = data.find(d => d.month === qDate.getMonth() && d.year === qDate.getFullYear());
        if (match) {
          const user = users.find(u => u.id === q.userId);
          if (user) {
            match[user.name] = (match[user.name] || 0) + (q.total || 0);
          }
        }
      }
    });
    return data;
  }, [quotes, users, currentUser]);

  const adminProductData = useMemo(() => {
    if (currentUser?.role !== 'admin') return [];
    const productSales = {};
    quotes.forEach(q => {
      if (q.status !== 'Rejected') {
        const user = users.find(u => u.id === q.userId)?.name || 'Unknown';
        q.items.forEach(item => {
          if (!productSales[item.name]) productSales[item.name] = { totalRevenue: 0, byUser: {} };
          const qty = Number(item.qty) || 1;
          const price = Number(item.price) || 0;
          productSales[item.name].totalRevenue += (price * qty);
          productSales[item.name].byUser[user] = (productSales[item.name].byUser[user] || 0) + (price * qty);
        });
      }
    });
    return Object.entries(productSales)
      .map(([name, stats]) => ({ name, ...stats }))
      .sort((a, b) => b.totalRevenue - a.totalRevenue)
      .slice(0, 10); // Top 10 products globally
  }, [quotes, users, currentUser]);


  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`p-4 rounded-xl shadow-xl backdrop-blur-md ${theme === 'dark' || theme === 'midnightNeon' ? 'bg-slate-900/90 text-white' : 'bg-white/90 text-slate-800'}`}>
          <p className="font-bold text-sm mb-2 pb-2 border-b border-white/20">{label}</p>
          {payload.map((entry, index) => (
             <div key={`item-${index}`} className="flex items-center gap-2 mb-1">
               <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
               <span className="text-xs font-semibold">{entry.name}:</span>
               <span className="text-xs font-black" style={{ color: entry.color }}>{formatCurrency(entry.value, 'INR')}</span>
             </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-8 space-y-8 animate-in fade-in">
      <div>
        <h2 className={`text-4xl font-black ${THEMES[theme].heading}`}>{currentUser?.role === 'admin' ? 'Company Analytics' : 'My Performance'}</h2>
        <p className={`${THEMES[theme].textMuted} font-medium mt-2`}>Your business metrics at a glance.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6" theme={theme}>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-blue-100 text-blue-600 rounded-xl"><TrendingUp size={24} /></div>
            <h3 className={`font-bold ${THEMES[theme].textMuted}`}>6-Month Revenue</h3>
          </div>
          <p className={`text-3xl font-black ${THEMES[theme].heading}`}>{formatCurrency(totalRevenue, 'INR')}</p>
        </Card>
        <Card className="p-6" theme={theme}>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-emerald-100 text-emerald-600 rounded-xl"><FileText size={24} /></div>
            <h3 className={`font-bold ${THEMES[theme].textMuted}`}>Total Quotes</h3>
          </div>
          <p className={`text-3xl font-black ${THEMES[theme].heading}`}>{myQuotes.length}</p>
        </Card>
        <Card className="p-6" theme={theme}>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-purple-100 text-purple-600 rounded-xl"><Users size={24} /></div>
            <h3 className={`font-bold ${THEMES[theme].textMuted}`}>Total Customers</h3>
          </div>
          <p className={`text-3xl font-black ${THEMES[theme].heading}`}>{myCustomers.length}</p>
        </Card>
        <Card className="p-6" theme={theme}>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-orange-100 text-orange-600 rounded-xl"><Package size={24} /></div>
            <h3 className={`font-bold ${THEMES[theme].textMuted}`}>Total Products</h3>
          </div>
          <p className={`text-3xl font-black ${THEMES[theme].heading}`}>{products.length}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Card className="p-8 lg:col-span-2" theme={theme}>
          <h3 className={`text-xl font-black mb-8 ${THEMES[theme].heading}`}>Revenue Trend (Last 6 Months)</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueData}>
                <XAxis dataKey="name" stroke={theme === 'dark' || theme === 'midnightNeon' ? '#64748b' : '#94a3b8'} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: 'transparent' }} content={<CustomTooltip />} />
                <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                  {revenueData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={theme === 'dark' || theme === 'midnightNeon' ? '#ea580c' : '#3b82f6'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-8" theme={theme}>
          <h3 className={`text-xl font-black mb-8 ${THEMES[theme].heading}`}>Quote Conversion</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={conversionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  stroke="none"
                >
                  {conversionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ borderRadius: '1rem', border: 'none', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                  itemStyle={{ fontWeight: 'bold' }}
                />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-8" theme={theme}>
        <h3 className={`text-xl font-black mb-6 ${THEMES[theme].heading}`}>Top Performing Products</h3>
        <div className="space-y-4">
          {topProducts.length > 0 ? topProducts.map((p, i) => (
            <div key={i} className={`p-4 rounded-2xl flex justify-between items-center ${theme === 'dark' || theme === 'midnightNeon' ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-black text-sm">{i + 1}</div>
                <div>
                  <h4 className={`font-bold ${THEMES[theme].text}`}>{p.name}</h4>
                  <p className={`text-xs ${THEMES[theme].textMuted}`}>{p.count} units quoted</p>
                </div>
              </div>
              <div className={`font-black ${THEMES[theme].heading}`}>
                {formatCurrency(p.revenue, 'INR')}
              </div>
            </div>
          )) : (
            <p className={THEMES[theme].textMuted}>No product data available yet.</p>
          )}
        </div>
      </Card>

      {/* ADMIN ONLY SECTIONS */}
      {currentUser?.role === 'admin' && (
        <div className="mt-12 space-y-8 animate-in slide-in-from-bottom-8">
           <div className="flex items-center gap-3 py-4 border-b border-orange-500/20">
              <ShieldAlert className="text-orange-500" size={28} />
              <h2 className={`text-2xl font-black ${THEMES[theme].heading}`}>Administrator Insights</h2>
           </div>

           <Card className="p-8" theme={theme}>
              <h3 className={`text-xl font-black mb-8 ${THEMES[theme].heading}`}>Team Performance (Monthly Revenue)</h3>
              <div className="h-[400px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={adminMonthlyData}>
                    <XAxis dataKey="name" stroke={theme === 'dark' || theme === 'midnightNeon' ? '#64748b' : '#94a3b8'} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: 'transparent' }} content={<CustomTooltip />} />
                    <Legend />
                    {users.map((u, i) => (
                      <Bar key={u.id} dataKey={u.name} stackId="a" fill={COLORS[i % COLORS.length]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
           </Card>

           <Card className="p-8" theme={theme}>
              <div className="flex items-center gap-3 mb-6">
                 <div className="p-2 bg-purple-100 text-purple-600 rounded-xl"><BarChart3 size={20} /></div>
                 <h3 className={`text-xl font-black ${THEMES[theme].heading}`}>Product Sales Breakdown (By User)</h3>
              </div>
              
              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-sm text-left">
                  <thead className={THEMES[theme].tableHeader}>
                    <tr>
                      <th className="px-6 py-4">Product Name</th>
                      <th className="px-6 py-4 text-right">Global Revenue</th>
                      {users.map(u => (
                        <th key={u.id} className="px-6 py-4 text-center">{u.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100/10">
                    {adminProductData.length > 0 ? adminProductData.map(p => (
                       <tr key={p.name} className={THEMES[theme].tableRow}>
                         <td className={`px-6 py-4 font-bold ${THEMES[theme].text}`}>{p.name}</td>
                         <td className="px-6 py-4 text-right font-black text-blue-600">{formatCurrency(p.totalRevenue, 'INR')}</td>
                         {users.map(u => (
                           <td key={u.id} className={`px-6 py-4 text-center font-medium ${THEMES[theme].textMuted}`}>
                             {p.byUser[u.name] ? formatCurrency(p.byUser[u.name], 'INR') : '-'}
                           </td>
                         ))}
                       </tr>
                    )) : (
                      <tr>
                        <td colSpan={users.length + 2} className={`px-6 py-8 text-center ${THEMES[theme].textMuted}`}>
                          No product sales data found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
           </Card>
        </div>
      )}
    </div>
  );
};

export default AnalyticsView;
